import React from "react";
import { Field, inputStyle, primaryButtonStyle } from "./StepEnergy";
import { LOAD_PROFILES } from "./calc";

/**
 * Step 2 — Your site.
 *
 * Load profile · roof space OR peak load · existing solar? (unlocks BO).
 */
export default function StepSite({ values, onChange, onNext, onBack }) {
  const setVal = (patch) => onChange({ ...values, ...patch });
  const canProceed =
    values.loadProfile && ((values.roofSqm || 0) > 0 || (values.peakLoadKw || 0) > 0);

  return (
    <div data-testid="audit-step-site">
      <div className="gg-eyebrow" style={{ marginBottom: 10 }}>
        Step 2 of 3
      </div>
      <h2 className="gg-h1" style={{ maxWidth: "24ch" }}>
        Your site — what are we sizing?
      </h2>
      <p
        style={{
          color: "var(--text-dim)",
          fontSize: 15.5,
          lineHeight: 1.7,
          maxWidth: 640,
          marginTop: 14,
        }}
      >
        Give us either the space you have or the peak load you draw — one is
        enough. If you already generate your own solar, we'll size a BESS-only
        system.
      </p>

      <div style={{ marginTop: 32 }}>
        <div className="gg-eyebrow" style={{ marginBottom: 12, fontSize: 11 }}>
          Load profile
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: 12,
            maxWidth: 780,
          }}
          data-testid="audit-load-profiles"
        >
          {Object.entries(LOAD_PROFILES).map(([id, p]) => {
            const active = values.loadProfile === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setVal({ loadProfile: id })}
                data-testid={`audit-profile-${id}`}
                style={{
                  padding: 14,
                  borderRadius: 12,
                  background: active
                    ? "linear-gradient(180deg, rgba(244,169,31,0.14), rgba(244,169,31,0.03))"
                    : "rgba(255,255,255,0.03)",
                  border: `1px solid ${
                    active ? "rgba(244,169,31,0.5)" : "var(--line)"
                  }`,
                  color: active ? "var(--gold-soft)" : "var(--text-cloud)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                  fontSize: 14,
                  lineHeight: 1.4,
                  transition: "border-color 200ms, background 200ms",
                }}
              >
                <div style={{ fontWeight: 600 }}>{p.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 20,
          marginTop: 32,
          maxWidth: 780,
        }}
      >
        <Field
          label="Available roof / ground space (sqm)"
          hint="Roughly 100 sqft ≈ 9.3 sqm."
        >
          <input
            type="number"
            min={0}
            value={values.roofSqm}
            onChange={(e) =>
              setVal({ roofSqm: parseFloat(e.target.value) || 0 })
            }
            data-testid="audit-input-roof"
            style={inputStyle}
          />
        </Field>

        <Field label="OR peak load (kW)" hint="Whichever you know.">
          <input
            type="number"
            min={0}
            value={values.peakLoadKw}
            onChange={(e) =>
              setVal({ peakLoadKw: parseFloat(e.target.value) || 0 })
            }
            data-testid="audit-input-peak-load"
            style={inputStyle}
          />
        </Field>

        <Field label="Existing on-site solar?">
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { v: false, label: "No" },
              { v: true, label: "Yes — BESS only" },
            ].map((opt) => {
              const active = values.existingSolar === opt.v;
              return (
                <button
                  key={String(opt.v)}
                  type="button"
                  onClick={() => setVal({ existingSolar: opt.v })}
                  data-testid={`audit-existing-solar-${opt.v ? "yes" : "no"}`}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: active
                      ? "rgba(244,169,31,0.12)"
                      : "rgba(255,255,255,0.03)",
                    border: `1px solid ${
                      active ? "rgba(244,169,31,0.5)" : "var(--line)"
                    }`,
                    color: active ? "var(--gold-soft)" : "var(--text-cloud)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 14,
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </Field>
      </div>

      <div
        style={{
          marginTop: 40,
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          data-testid="audit-back-2"
          style={{
            ...primaryButtonStyle(false),
            background: "transparent",
            color: "var(--text-dim)",
            borderColor: "var(--line)",
            boxShadow: "none",
          }}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          data-testid="audit-next-2"
          style={primaryButtonStyle(!canProceed)}
        >
          See what you could save →
        </button>
      </div>
    </div>
  );
}
